"use client";

import posthog, { type CaptureOptions, type CaptureResult, type EventName, type Properties } from "posthog-js";
import { useEffect } from "react";

import { simpleDecryptJson, simpleEncryptJson } from "@/lib/utils/json-encrypt";
import { localStorageLastUser } from "@/lib/utils/storage-apis";

import { useGetEmployee } from "@/subsystems/user/employees/hooks";
import { useGetOrganization } from "@/subsystems/user/organizations/hooks";

declare global {
  interface Window {
    posthog?: typeof posthog; // only attached to window in non-production environments for e2e tests
  }
}

type LastKnownUser = {
  groupKey?: string | undefined;
  groupPropertiesToSet?: Record<string, unknown>;
  groupType?: string | undefined;
  new_distinct_id?: string | undefined;
  userPropertiesToSet?: Record<string, unknown>;
};

export function PostHogIdentifyLastKnownUser() {
  useIdentifyLastKnown();

  return null;
}

export function PostHogIdentifyName() {
  useIdentifyName();

  return null;
}

function useIdentifyLastKnown() {
  useEffect(function addToPostHog() {
    // Extract all complex logic outside of try/catch
    const rawEncryptedData = localStorageLastUser.get();
    const encryptedLastKnown = rawEncryptedData || "";

    let decryptedLastKnown: LastKnownUser = {};

    // Handle decryption safely outside of main try/catch
    if (encryptedLastKnown) {
      try {
        const decryptResult = simpleDecryptJson(String(encryptedLastKnown));
        // Handle null/undefined result outside of try/catch logic
        if (decryptResult) {
          decryptedLastKnown = decryptResult;
        } else {
          decryptedLastKnown = {};
        }
      } catch (decryptError) {
        console.warn("Failed to decrypt last known user data:", decryptError);
        decryptedLastKnown = {};
      }
    }

    // Extract properties outside of try/catch
    const { new_distinct_id, userPropertiesToSet } = decryptedLastKnown;
    const { groupKey, groupPropertiesToSet, groupType } = decryptedLastKnown;

    // Check conditions outside of try/catch
    const shouldIdentifyUser = new_distinct_id && userPropertiesToSet;
    const shouldIdentifyGroup = groupType && groupKey && groupPropertiesToSet;

    // Simple try/catch with minimal logic
    try {
      if (shouldIdentifyUser) {
        posthog.identify(new_distinct_id, userPropertiesToSet);
      }

      if (shouldIdentifyGroup) {
        posthog.group(groupType, groupKey, groupPropertiesToSet);
      }
    } catch (error) {
      console.error("Failed to identify last known user with PostHog:", error);
    }
  }, []);
}

function useIdentifyName() {
  const {
    employee: { data: employeeData, isLoading: employeeIsLoading },
  } = useGetEmployee();
  const { organization: organizationData, organizationIsLoading } = useGetOrganization();

  useEffect(
    function addNameToPostHog() {
      if (!employeeIsLoading || !organizationIsLoading) {
        const userPk = employeeData?.pk;
        const new_distinct_id = userPk;

        if (!new_distinct_id) {
          return;
        }

        // Extract all complex logic outside of try/catch
        const email = employeeData?.email;
        const firstName = employeeData?.firstName;
        const lastName = employeeData?.lastName;
        const userAuthId = employeeData?.authUserId;
        const userComplianceVerificationStatus = employeeData?.complianceVerificationStatus;
        const organizationName = organizationData?.name;
        const organizationPk = organizationData?.pk;
        const organizationAuthId = organizationData?.authOrganizationId;

        // Pre-compute the full name outside of try/catch
        const nameComponents = [firstName, lastName].filter(Boolean);
        const fullName = nameComponents.join(" ").trim();

        // Define key-value pairs outside of try/catch
        const propertyPairs: [string, string | undefined][] = [
          ["email", email],
          ["name", fullName],
          ["firstName", firstName],
          ["lastName", lastName],
          ["userAuthId", userAuthId],
          ["userComplianceVerificationStatus", userComplianceVerificationStatus],
          ["userPk", userPk],
          ["organizationName", organizationName],
          ["organizationPk", organizationPk],
          ["organizationAuthId", organizationAuthId],
        ];

        // Extract organization data outside of try/catch
        const shouldIdentifyGroup = Boolean(organizationPk);
        const orgAuthId = organizationData?.authOrganizationId;
        const orgName = organizationData?.name;

        // Pre-compute organization group data outside of try/catch
        const groupType = "organization";
        const groupPropertiesToSet = {
          authId: orgAuthId,
          name: orgName,
          pk: organizationPk,
        };

        try {
          const userPropertiesToSet: Record<string, string> = {};

          // Populate properties using a simple loop
          propertyPairs.forEach(([key, value]) => {
            if (value) {
              userPropertiesToSet[key] = value;
            }
          });

          const toLog: LastKnownUser = {
            groupKey: undefined,
            groupPropertiesToSet: undefined,
            groupType: undefined,
            new_distinct_id,
            userPropertiesToSet,
          };

          posthog.identify(new_distinct_id, userPropertiesToSet);

          if (shouldIdentifyGroup) {
            toLog.groupType = groupType;
            toLog.groupKey = organizationPk;
            toLog.groupPropertiesToSet = groupPropertiesToSet;

            // TypeScript knows organizationPk is truthy here due to shouldIdentifyGroup check
            posthog.group(groupType, organizationPk as string, groupPropertiesToSet);
          }

          localStorageLastUser.set(simpleEncryptJson(toLog));
        } catch (error) {
          console.error("Failed to identify user with PostHog:", error);
        }
      }
    },
    [employeeData, employeeIsLoading, organizationData, organizationIsLoading],
  );
}

export function posthogLogError(
  event_name: EventName,
  properties?: Properties | null,
  options?: CaptureOptions,
): CaptureResult | undefined {
  return posthog.capture(event_name, properties, options);
}
